<template>
    <div class="dealers-app__selected-dealer">
        <div class="dealers-app__selected-dealer--dealer-info">
            <div class="dealers-app__selected-dealer--features">
                <div v-if="selectedDealer?.onlineRetailer" class="dealers-app__selected-dealer--online-badge badge">
                    <Earth size="20" color="#ff7f33" />
                    <p>Online Retailer</p>
                </div>
                <div v-if="selectedDealer?.location?.sellsVersiflex" class="dealers-app__selected-dealer--versiflex-badge badge">
                    <Star color="#e0457b" />
                    <p>VersiFlex dealer!</p>
                </div>
                <div v-if="selectedDealer?.location?.pickupLocation" class="dealers-app__selected-dealer--pickup-badge badge">
                    <Truck color="#136362" />
                    <p>Pickup Location</p>
                </div>
            </div>
            <div class="dealers-app__selected-dealer--info">
                <div class="dealers-app__selected-dealer--info-header">
                    <h3 class="dealers-app__selected-dealer--name">{{ selectedDealer?.dealerName || 'Unknown Dealer' }}</h3>
                    <span v-if="selectedDealer?.distance !== null && selectedDealer?.distance !== undefined && !selectedDealer?.onlineRetailer" class="distance-badge">
                        {{ Math.round(selectedDealer.distance * 10) / 10 }} miles away
                    </span>
                </div>
                <div v-if="!selectedDealer?.onlineRetailer || dealerAddress" class="dealers-app__selected-dealer--address">
                    <h3 class="dealers-app__selected-dealer--address-tittle">Address:</h3>
                    <p class="dealers-app__selected-dealer--address-detail">{{ dealerAddress || 'Online Only' }}</p>
                    <p class="dealers-app__selected-dealer--country">{{ selectedDealer?.location?.address?.country || '' }}</p>
                </div>
                <div v-if="selectedDealer?.location?.address?.phone" class="dealers-app__selected-dealer--phone">
                    <h3 class="dealers-app__selected-dealer--phone-tittle">Phone:</h3>
                    <p class="dealers-app__selected-dealer--phone-detail">{{ selectedDealer?.location?.address?.phone || '' }}</p>
                </div>
                <div v-if="selectedDealer?.location?.availableProducts?.length" class="dealers-app__selected-dealer--products">
                    <h3 class="dealers-app__selected-dealer--products-title">Available Products:</h3>
                    <div class="dealers-app__selected-dealer--products-container">
                        <span
                            v-for="product in selectedDealer.location.availableProducts"
                            :key="product?.title || product"
                            class="dealers-app__selected-dealer--product">
                            <CheckCheck color="#009945" />
                            {{ product?.title || product }}
                        </span>
                    </div>
                </div>
                <div class="dealers-app__selected-dealer--ctas">
                    <a v-if="selectedDealer?.location?.website" :href="`${selectedDealer.location.website}`" target="_blank" class="button dealers-app__selected-dealer--website">
                        <PanelsTopLeft path="#e0457b" />
                        {{ selectedDealer?.onlineRetailer ? 'Visit Online Store' : 'Dealer Website' }}
                    </a>
                    <a v-if="selectedDealer?.directionsLink && !selectedDealer?.onlineRetailer" :href="`${selectedDealer.directionsLink}`" target="_blank" class="button dealers-app__selected-dealer--website">
                        <SquareArrowRight path="#e0457b" />
                        Directions
                    </a>
                </div>
            </div>
        </div>
        <Map :location="selectedDealer"></map>
    </div>
</template>
<script setup>
import { PanelsTopLeft, SquareArrowRight, Star, Truck, CheckCheck, MapPinHouse, Earth } from 'lucide-vue-next';
import { ref, onMounted, watch, computed } from 'vue';
import Map from '../components/Map.vue';

const props = defineProps({
    selectedDealer: Object,
    filtersContainer: Object
})

const dealerAddress = computed(() => {
    if (!props.selectedDealer?.location?.address) {
        return '';
    }
    
    const address = props.selectedDealer.location.address;
    const city = address.city || '';
    const state = address.state || '';
    const street = address.street || '';
    
    return `${city}, ${state}, ${street}`.replace(/^,\s*|,\s*$/, '').replace(/,\s*,/g, ',');
});

onMounted( async () => {
    // set the width of the selectedDealerContainer to be 66% of filtersContainer width  for screen sizes resolution above 1024px

    try {
        if (props.filtersContainer && window.innerWidth > 1024) {

            const dealersListWidth = props.filtersContainer.offsetWidth || 0;
            const selectedDealerContainer = document.querySelector('.dealers-app__selected-dealer');
            if (selectedDealerContainer) {
                //get the 66% of the filtersContainer width
                const containerWidth = props.filtersContainer.offsetWidth * 0.66;
                selectedDealerContainer.style.width = `${containerWidth}px`;
            }
        }
    }
    catch (error) {
        console.error('Error setting up selected dealer width:', error);
    }
});

</script>
<style lang="scss" scoped>

.dealers-app {
    &__selected-dealer {
        display: grid;
        grid-template-areas: "info map";
        grid-template-columns: 50% 47.7%;
        gap: 20px;
        min-height: 100%;
        height: 100%; 

        &--dealer-info {
            grid-area: "info";
            padding: 2rem;

            @media screen and (max-width: 640px) {
                padding: 0 0 1rem 0;
            }
        }

        .dealers-app__map {
            grid-area: "map";
            background-color: #eee;

            @media screen and (max-width: 640px) {
                height: 350px;
                min-height: 350px;
                width: 100%;
                max-width: 100%;
            }   
        }

        @media screen and (max-width: 640px) {
            grid-template-areas: "info" "map";
            grid-template-columns: 100%;
            padding: 1rem;
        }
    }

    &__selected-dealer {
        &--dealer {
            display: flex;
            flex-direction: column;
            border-bottom: 2px solid rgb(236, 236, 236);
            border-left: 2px solid rgb(236, 236, 236);
            border-right: 2px solid rgb(236, 236, 236);
            overflow: hidden;
            border-color: #e5e5e5;

            &:first-child {
                border-top: 2px solid rgb(236, 236, 236);
                border-top-right-radius: 6px;
                border-top-right-radius: 6px;
            }

            &:last-child {
                border-bottom-right-radius: 6px;
                border-bottom-left-radius: 6px;
            }
        }

        &--info {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }

        &--info-header {
            display: flex;
            align-items: baseline;
            flex-direction: column;

            .distance-badge {
                font-size: 16px;
                color: #b9b9b9;
                font-weight: 400;
            }
        }

        &--features {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            margin-bottom: 1rem;

            .badge {
                position: relative;
                padding: 5px 10px;
                display: flex;
                gap: 10px;
                border-radius: 6px;
                justify-content: center;
                align-items: center;

                p {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 600;
                }

                img {
                    width: 100%;
                    height: auto;
                    max-width: 20px;
                }
            }
        }

        &--online-badge {
            background-color: #fff0e6;

            p {
                color: #ff7f32;
            }
        }

        &--versiflex-badge {
            &.badge {
                padding-left: 0;
            }

            p {
                color: #e0457b;
            }
        }

        &--pickup-badge {
            background-color: #CAEEED;

            p {
                color: #136362;
            }
        }

        &--content {
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
            padding: 1rem 2rem 2rem;
        }

        &--name {
            font-size: 24px;
            margin: 0;
        }

        &--country {
            margin: 0;
        }

        &--address {
            margin: 0;
            padding: 0;
            color: #4d4e53;
            font-size: 1rem;
            font-weight: 100;
        }

        &--phone {
            margin: 0 0 0 0;
            padding: 0;
            color: #4d4e53;
            font-size: 1rem;
            font-weight: 200;
        }

        &--ctas {
            display: flex;
            gap: 1rem;
            flex-wrap: wrap;
        }

        &--website {
            color: #e0457b;
            text-decoration: none;
            display: flex;
            gap: 10px;
            border: 1px solid #e0457b;
            padding: 10px 20px;
            border-radius: 150px;
            background-color: transparent;
            font-size: 1rem;
        }

        &--products-container {
            display: flex;
            gap: 7px;
            flex-wrap: wrap;
            margin-bottom: 1rem;
        }

        &--products {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            flex-direction: column;
        }

        &--products-title,
        &--address-tittle,
        &--phone-tittle {
            font-size: 18px;
            margin-bottom: 0;
            margin-top: 0;
        }

        &--address,
        &--phone {
            p {
                padding: 0;
                margin: 0;
            }
        }

        &--product {
            border-radius: 30px;
            background-color: #ececec;
            padding: 5px 20px;
            color: #333;
            font-size: .9rem;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 5px;
        }
    }
}
</style>